/**
 * MoreGames.jsx
 *
 * Client-side spiritual mini-games. No backend persistence required — they
 * use localStorage for high scores so they survive between sessions.
 *
 * Each component is self-contained and can be rendered in isolation.
 */
import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles, Star, Zap, Dices, Box, RotateCw, Loader2,
  Trophy, ChevronRight, Check, X as XIcon, Compass,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

/* -------------------------------------------------------------------------- */
/* shared helpers                                                             */
/* -------------------------------------------------------------------------- */
const lsGet = (key, def) => {
  try {
    const v = localStorage.getItem(key);
    return v ? JSON.parse(v) : def;
  } catch {
    return def;
  }
};
const lsSet = (key, val) => {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch { /* noop */ }
};
const rand = (arr) => arr[Math.floor(Math.random() * arr.length)];
const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);

/* ========================================================================== */
/* 1. PÉNDULO ESPIRITUAL                                                      */
/* ========================================================================== */
export const PendulumGame = () => {
  const [question, setQuestion] = useState("");
  const [phase, setPhase] = useState("idle"); // idle | swinging | revealed
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState(() => lsGet("game_pendulum_hist", []));

  const ask = () => {
    if (!question.trim() || phase !== "idle") return;
    setPhase("swinging");
    setResult(null);
    const moods = [
      { mood: "yes", text: "Sí, claro.", swing: "yes" },
      { mood: "yes", text: "Definitivamente.", swing: "yes" },
      { mood: "yes", text: "Avanza con confianza.", swing: "yes" },
      { mood: "no", text: "No es el momento.", swing: "no" },
      { mood: "no", text: "Aléjate de eso.", swing: "no" },
      { mood: "no", text: "Aún no, espera.", swing: "no" },
      { mood: "maybe", text: "El universo aún no decide.", swing: "maybe" },
      { mood: "maybe", text: "Reformula tu pregunta.", swing: "maybe" },
    ];
    const picked = rand(moods);
    setTimeout(() => {
      setResult(picked);
      setPhase("revealed");
      const newHist = [{ q: question, ...picked, at: Date.now() }, ...history].slice(0, 10);
      setHistory(newHist);
      lsSet("game_pendulum_hist", newHist);
    }, 3500);
  };

  const reset = () => {
    setPhase("idle");
    setResult(null);
    setQuestion("");
  };

  const swing = result?.swing || "maybe";
  const swingAnim =
    swing === "yes"   ? { rotate: [0, -25, 25, -25, 25, 0] } :
    swing === "no"    ? { rotate: [0, -40, 40, -40, 40, 0] } :
                        { rotate: [0, 360] };

  return (
    <div className="space-y-6 max-w-md mx-auto" data-testid="game-pendulum">
      <div className="text-center">
        <h2 className="font-serif text-xl text-white mb-1">El Péndulo Espiritual</h2>
        <p className="text-xs text-neutral-400">Pregunta y observa el balanceo del péndulo.</p>
      </div>

      {/* Pendulum SVG */}
      <div className="flex justify-center">
        <svg width="220" height="240" viewBox="0 0 220 240">
          <line x1="110" y1="0" x2="110" y2="20" stroke="#d4af37" strokeWidth="2" />
          <circle cx="110" cy="20" r="4" fill="#d4af37" />
          <motion.g
            style={{ originX: "110px", originY: "20px" }}
            animate={phase === "swinging" ? { rotate: [0, -30, 30, -30, 30, -15, 15, 0] } :
                     phase === "revealed" ? swingAnim : { rotate: 0 }}
            transition={phase === "swinging"
              ? { duration: 3.5, ease: "easeInOut" }
              : { duration: 1.2, ease: "easeOut" }}
          >
            <line x1="110" y1="20" x2="110" y2="160" stroke="#d4af37" strokeWidth="1.5" opacity="0.6" />
            <motion.g
              animate={phase === "revealed"
                ? { scale: [1, 1.4, 1], filter: ["drop-shadow(0 0 0px #d4af37)", "drop-shadow(0 0 24px #d4af37)", "drop-shadow(0 0 6px #d4af37)"] }
                : {}}
              transition={{ duration: 1.5 }}
            >
              <circle cx="110" cy="180" r="22" fill="url(#gemGrad)" stroke="#d4af37" strokeWidth="2" />
              <circle cx="105" cy="172" r="5" fill="rgba(255,255,255,0.7)" />
            </motion.g>
          </motion.g>
          <defs>
            <radialGradient id="gemGrad" cx="40%" cy="35%">
              <stop offset="0%" stopColor="#fff7d6" />
              <stop offset="60%" stopColor="#d4af37" />
              <stop offset="100%" stopColor="#5b3a05" />
            </radialGradient>
          </defs>
        </svg>
      </div>

      {phase === "idle" && (
        <div className="flex gap-2">
          <Input
            data-testid="pendulum-question"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Pregunta sí/no..."
            className="bg-black/40 border-white/10 text-white"
            onKeyDown={(e) => e.key === "Enter" && ask()}
          />
          <button data-testid="pendulum-ask" onClick={ask} disabled={!question.trim()} className="btn-gold px-4 rounded-lg disabled:opacity-50">
            Preguntar
          </button>
        </div>
      )}

      {phase === "swinging" && (
        <p className="text-center text-xs text-[#d4af37] animate-pulse">El péndulo escucha tu pregunta…</p>
      )}

      <AnimatePresence>
        {phase === "revealed" && result && (
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className={`rounded-xl border p-4 text-center ${
              result.mood === "yes" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200" :
              result.mood === "no"  ? "border-red-500/40 bg-red-500/10 text-red-200" :
                                       "border-amber-500/40 bg-amber-500/10 text-amber-200"
            }`}
            data-testid="pendulum-result"
          >
            <p className="font-serif text-lg">{result.text}</p>
            <button onClick={reset} className="mt-3 text-[10px] uppercase tracking-widest text-neutral-300 hover:text-white border border-white/10 rounded-full px-3 py-1">
              Otra pregunta
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {history.length > 0 && (
        <div className="mt-4">
          <h3 className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Últimas preguntas</h3>
          <div className="space-y-1.5">
            {history.slice(0, 5).map((h) => (
              <div key={h.at} className="text-[11px] bg-black/40 border border-white/5 rounded-md px-2.5 py-1.5">
                <span className="text-neutral-400">{h.q}</span>{" "}
                <span className={h.mood === "yes" ? "text-emerald-300" : h.mood === "no" ? "text-red-300" : "text-amber-300"}>→ {h.text}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ========================================================================== */
/* 2. TIRADA DE RUNAS                                                         */
/* ========================================================================== */
const RUNES = [
  { sym: "ᚠ", name: "Fehu",     msg: "Abundancia material y nuevos recursos en camino." },
  { sym: "ᚢ", name: "Uruz",     msg: "Fuerza interior. Confía en tu poder personal." },
  { sym: "ᚦ", name: "Thurisaz", msg: "Cuidado con conflictos. Reflexiona antes de actuar." },
  { sym: "ᚨ", name: "Ansuz",    msg: "Llegará un mensaje importante. Escucha con atención." },
  { sym: "ᚱ", name: "Raidho",   msg: "Un viaje físico o espiritual está por iniciar." },
  { sym: "ᚲ", name: "Kenaz",    msg: "Claridad. Una verdad oculta se revela." },
  { sym: "ᚷ", name: "Gebo",     msg: "Un regalo o asociación importante se acerca." },
  { sym: "ᚹ", name: "Wunjo",    msg: "Alegría y armonía vuelven a tu vida." },
  { sym: "ᚺ", name: "Hagalaz",  msg: "Un cambio disruptivo te liberará de lo viejo." },
  { sym: "ᚾ", name: "Nauthiz",  msg: "Necesidad. Paciencia mientras pasa la prueba." },
  { sym: "ᛁ", name: "Isa",      msg: "Pausa. Es momento de quietud y observación." },
  { sym: "ᛃ", name: "Jera",     msg: "Cosecha. El trabajo previo da frutos." },
  { sym: "ᛇ", name: "Eihwaz",   msg: "Transformación profunda en proceso." },
  { sym: "ᛈ", name: "Perthro",  msg: "El destino juega cartas inesperadas a tu favor." },
  { sym: "ᛉ", name: "Algiz",    msg: "Protección espiritual rodea tu camino." },
  { sym: "ᛋ", name: "Sowilo",   msg: "Éxito brillante. Tu sol se eleva." },
  { sym: "ᛏ", name: "Tiwaz",    msg: "Victoria si actúas con justicia y coraje." },
  { sym: "ᛒ", name: "Berkano",  msg: "Crecimiento, nacimiento o fertilidad creativa." },
  { sym: "ᛖ", name: "Ehwaz",    msg: "Movimiento. Confía en tus aliados." },
  { sym: "ᛗ", name: "Mannaz",   msg: "Conócete a ti mismo. Allí está la respuesta." },
  { sym: "ᛚ", name: "Laguz",    msg: "Sigue tu intuición; las aguas te guían." },
  { sym: "ᛜ", name: "Ingwaz",   msg: "Energía contenida lista para florecer." },
  { sym: "ᛞ", name: "Dagaz",    msg: "Amanecer. Un nuevo ciclo de luz comienza." },
  { sym: "ᛟ", name: "Othala",   msg: "Hogar y herencia. Honra tus raíces." },
];

export const RuneCastGame = () => {
  const [phase, setPhase] = useState("ready"); // ready | drawn
  const [runes, setRunes] = useState([]);
  const [revealed, setRevealed] = useState([false, false, false]);

  const draw = () => {
    const picked = shuffle(RUNES).slice(0, 3);
    setRunes(picked);
    setRevealed([false, false, false]);
    setPhase("drawn");
  };

  const reveal = (idx) => {
    setRevealed((r) => r.map((v, i) => (i === idx ? true : v)));
  };

  const labels = ["Pasado", "Presente", "Futuro"];

  return (
    <div className="space-y-6 max-w-2xl mx-auto" data-testid="game-runes">
      <div className="text-center">
        <h2 className="font-serif text-xl text-white mb-1">Tirada de Runas Nórdicas</h2>
        <p className="text-xs text-neutral-400">Tres runas para guiar tu camino: pasado, presente, futuro.</p>
      </div>

      {phase === "ready" && (
        <div className="text-center">
          <motion.div
            animate={{ rotate: [0, 360] }}
            transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
            className="w-32 h-32 mx-auto rounded-full border-2 border-[#d4af37]/40 flex items-center justify-center"
          >
            <Compass className="w-16 h-16 text-[#d4af37]" />
          </motion.div>
          <button onClick={draw} data-testid="runes-draw" className="btn-gold mt-6 px-6 py-3 rounded-full uppercase tracking-widest text-xs">
            Lanzar las runas
          </button>
        </div>
      )}

      {phase === "drawn" && (
        <>
          <div className="grid grid-cols-3 gap-3">
            {runes.map((r, i) => (
              <div key={i} className="text-center">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">{labels[i]}</div>
                <motion.button
                  whileHover={{ scale: revealed[i] ? 1 : 1.05 }}
                  onClick={() => reveal(i)}
                  data-testid={`rune-${i}`}
                  className={`relative w-full aspect-[3/4] rounded-xl border-2 flex items-center justify-center transition-colors ${
                    revealed[i] ? "border-[#d4af37] bg-gradient-to-br from-[#3a2a05] to-black" : "border-[#d4af37]/30 bg-gradient-to-br from-[#1a0a1f] to-black"
                  }`}
                >
                  <AnimatePresence mode="wait">
                    {revealed[i] ? (
                      <motion.div
                        key="r"
                        initial={{ rotateY: 180, opacity: 0 }}
                        animate={{ rotateY: 0, opacity: 1 }}
                        transition={{ duration: 0.5 }}
                        className="text-center"
                      >
                        <div className="text-5xl text-[#d4af37] font-serif">{r.sym}</div>
                        <div className="text-[11px] text-white mt-2">{r.name}</div>
                      </motion.div>
                    ) : (
                      <motion.div key="b" exit={{ opacity: 0 }} className="text-[#d4af37]/40">
                        <Sparkles className="w-10 h-10" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.button>
                <AnimatePresence>
                  {revealed[i] && (
                    <motion.p
                      initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }}
                      className="text-[11px] text-neutral-300 italic mt-2 leading-snug"
                    >
                      {r.msg}
                    </motion.p>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
          <div className="text-center">
            <button onClick={draw} data-testid="runes-again" className="text-xs text-neutral-300 hover:text-white border border-white/10 rounded-full px-4 py-2 uppercase tracking-widest">
              Nueva tirada
            </button>
          </div>
        </>
      )}
    </div>
  );
};

/* ========================================================================== */
/* 3. COFRE DEL DESTINO                                                       */
/* ========================================================================== */
const TREASURES = [
  { theme: "amor",    text: "El amor toca a tu puerta. Recíbelo con el corazón abierto." },
  { theme: "amor",    text: "Una persona del pasado regresa con una verdad pendiente." },
  { theme: "dinero",  text: "Una oportunidad económica te encontrará pronto." },
  { theme: "dinero",  text: "Revisa tus cuentas: hay un detalle que ignoras." },
  { theme: "salud",   text: "Tu energía pide descanso. Honra tu cuerpo." },
  { theme: "salud",   text: "Una práctica diaria sanará lo que arrastras." },
  { theme: "trabajo", text: "Un nuevo proyecto encenderá tu pasión profesional." },
  { theme: "trabajo", text: "Pide ayuda. Hay alguien dispuesto a guiarte." },
  { theme: "viaje",   text: "Un viaje inesperado te transformará en este ciclo." },
  { theme: "familia", text: "La reconciliación llega. Da el primer paso." },
];

export const TreasureChestGame = () => {
  const [opened, setOpened] = useState(null);
  const [treasures, setTreasures] = useState(() => shuffle(TREASURES).slice(0, 3));

  const reset = () => {
    setOpened(null);
    setTreasures(shuffle(TREASURES).slice(0, 3));
  };

  return (
    <div className="space-y-6 max-w-md mx-auto text-center" data-testid="game-chest">
      <h2 className="font-serif text-xl text-white">Cofre del Destino</h2>
      <p className="text-xs text-neutral-400">Elige uno de los tres cofres. Solo uno guarda tu mensaje.</p>

      {opened === null ? (
        <div className="grid grid-cols-3 gap-3">
          {[0, 1, 2].map((i) => (
            <motion.button
              key={i}
              whileHover={{ scale: 1.08, y: -4 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setOpened(i)}
              data-testid={`chest-${i}`}
              className="aspect-square rounded-xl border-2 border-[#d4af37]/40 bg-gradient-to-br from-[#3a2a05] to-black flex items-center justify-center group hover:border-[#d4af37]"
            >
              <Box className="w-14 h-14 text-[#d4af37] group-hover:scale-110 transition-transform" />
            </motion.button>
          ))}
        </div>
      ) : (
        <motion.div
          initial={{ scale: 0, rotate: -30 }}
          animate={{ scale: 1, rotate: 0 }}
          className="rounded-2xl border-2 border-[#d4af37] bg-gradient-to-br from-amber-700/30 via-[#d4af37]/15 to-purple-900/20 p-6 shadow-[0_0_60px_rgba(212,175,55,0.5)]"
          data-testid="chest-revealed"
        >
          <Sparkles className="w-10 h-10 text-[#d4af37] mx-auto mb-3" />
          <p className="font-serif text-lg text-white leading-relaxed">{treasures[opened].text}</p>
          <div className="mt-2 text-[10px] uppercase tracking-widest text-[#d4af37]">{treasures[opened].theme}</div>
          <button onClick={reset} className="mt-5 text-xs text-neutral-300 hover:text-white border border-white/10 rounded-full px-4 py-2 uppercase tracking-widest">
            Otro cofre
          </button>
        </motion.div>
      )}
    </div>
  );
};

/* ========================================================================== */
/* 4. DADOS DEL UNIVERSO                                                      */
/* ========================================================================== */
const DICE_FACES = [
  { sym: "☉", name: "Sol",     msg: "Brillas. Es momento de mostrarte." },
  { sym: "☽", name: "Luna",    msg: "Honra tus emociones; te guían." },
  { sym: "★", name: "Estrella",msg: "Una oportunidad cósmica se aproxima." },
  { sym: "♆", name: "Mar",     msg: "Fluye con los cambios; no resistas." },
  { sym: "♀", name: "Venus",   msg: "El amor o la belleza entran en escena." },
  { sym: "♂", name: "Marte",   msg: "Actúa con coraje; no es momento de dudar." },
];

export const DiceGame = () => {
  const [dice, setDice] = useState([null, null, null]);
  const [rolling, setRolling] = useState(false);

  const roll = () => {
    if (rolling) return;
    setRolling(true);
    const ticks = 14;
    let i = 0;
    const t = setInterval(() => {
      setDice([rand(DICE_FACES), rand(DICE_FACES), rand(DICE_FACES)]);
      i += 1;
      if (i >= ticks) {
        clearInterval(t);
        setRolling(false);
      }
    }, 80);
  };

  return (
    <div className="space-y-6 max-w-md mx-auto text-center" data-testid="game-dice">
      <h2 className="font-serif text-xl text-white">Dados del Universo</h2>
      <p className="text-xs text-neutral-400">Tres dados, tres mensajes celestes.</p>

      <div className="grid grid-cols-3 gap-3">
        {dice.map((d, i) => (
          <motion.div
            key={i}
            animate={rolling ? { rotateX: [0, 360, 720], rotateY: [0, 360] } : {}}
            transition={{ duration: 1.1, ease: "easeOut" }}
            className="aspect-square rounded-xl border-2 border-[#d4af37]/40 bg-gradient-to-br from-[#1a0a1f] to-black flex items-center justify-center text-5xl text-[#d4af37] shadow-lg"
            data-testid={`dice-${i}`}
          >
            {d ? d.sym : "?"}
          </motion.div>
        ))}
      </div>

      <button onClick={roll} disabled={rolling} data-testid="dice-roll" className="btn-gold px-6 py-3 rounded-full uppercase tracking-widest text-xs disabled:opacity-50">
        <Dices className="w-4 h-4 inline mr-1" />
        {rolling ? "Lanzando..." : "Lanzar dados"}
      </button>

      <AnimatePresence>
        {!rolling && dice[0] && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-2 text-left">
            {dice.map((d, i) => (
              <div key={i} className="bg-black/40 border border-white/10 rounded-lg p-2.5 text-xs">
                <span className="text-[#d4af37] font-serif text-base mr-2">{d.sym}</span>
                <span className="text-white font-semibold">{d.name}:</span>{" "}
                <span className="text-neutral-300">{d.msg}</span>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ========================================================================== */
/* 5. REFLEJOS MÍSTICOS                                                       */
/* ========================================================================== */
export const ReflexGame = () => {
  const [phase, setPhase] = useState("idle"); // idle | waiting | go | done | tooSoon
  const [reactions, setReactions] = useState([]);
  const [round, setRound] = useState(0);
  const startRef = useRef(0);
  const timeoutRef = useRef(null);
  const [best, setBest] = useState(() => lsGet("game_reflex_best", null));

  const ROUNDS = 5;

  const next = () => {
    setPhase("waiting");
    const delay = 1200 + Math.random() * 2200;
    timeoutRef.current = setTimeout(() => {
      startRef.current = Date.now();
      setPhase("go");
    }, delay);
  };

  const onClick = () => {
    if (phase === "idle") {
      setReactions([]);
      setRound(0);
      next();
      return;
    }
    if (phase === "waiting") {
      clearTimeout(timeoutRef.current);
      setPhase("tooSoon");
      return;
    }
    if (phase === "go") {
      const ms = Date.now() - startRef.current;
      const r = [...reactions, ms];
      setReactions(r);
      const nextRound = round + 1;
      setRound(nextRound);
      if (nextRound >= ROUNDS) {
        setPhase("done");
        const avg = Math.round(r.reduce((a, b) => a + b, 0) / r.length);
        if (best === null || avg < best) {
          lsSet("game_reflex_best", avg);
          setBest(avg);
          toast.success(`🏆 ¡Nuevo récord! ${avg}ms promedio`);
        }
      } else {
        next();
      }
    }
  };

  const restart = () => {
    setPhase("idle");
    setReactions([]);
    setRound(0);
  };

  // Cleanup pending timeout when unmounting so we don't try to setState on a
  // detached component (e.g. user clicks "back" during the wait).
  useEffect(() => () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  const avg = reactions.length ? Math.round(reactions.reduce((a, b) => a + b, 0) / reactions.length) : 0;

  return (
    <div className="space-y-4 max-w-md mx-auto text-center" data-testid="game-reflex">
      <h2 className="font-serif text-xl text-white">Reflejos Místicos</h2>
      <p className="text-xs text-neutral-400">Espera la estrella dorada y toca lo más rápido que puedas. 5 rondas.</p>

      <button
        onClick={onClick}
        data-testid="reflex-target"
        className={`w-full aspect-square max-w-[260px] mx-auto rounded-2xl flex items-center justify-center border-2 transition-colors ${
          phase === "waiting" ? "bg-[#2a1a0a] border-amber-500/30" :
          phase === "go" ? "bg-[#d4af37] border-[#d4af37] animate-pulse" :
          phase === "tooSoon" ? "bg-red-500/20 border-red-400" :
          phase === "done" ? "bg-emerald-500/15 border-emerald-400" :
          "bg-black/40 border-white/10"
        }`}
      >
        {phase === "idle" && <Zap className="w-16 h-16 text-[#d4af37]/70" />}
        {phase === "waiting" && <span className="text-neutral-400 text-sm">Espera la estrella...</span>}
        {phase === "go" && <Star className="w-20 h-20 text-black" fill="black" />}
        {phase === "tooSoon" && <span className="text-red-300">¡Demasiado pronto!</span>}
        {phase === "done" && (
          <div className="text-emerald-200">
            <Trophy className="w-12 h-12 mx-auto mb-2" />
            <div className="font-serif text-2xl">{avg}ms</div>
            <div className="text-[10px] uppercase tracking-widest mt-1">Promedio</div>
          </div>
        )}
      </button>

      {phase !== "idle" && phase !== "done" && (
        <div className="text-xs text-neutral-400">Ronda {Math.min(round + 1, ROUNDS)} / {ROUNDS}</div>
      )}

      {phase === "done" && (
        <div className="space-y-2">
          <div className="flex justify-center gap-1.5 flex-wrap">
            {reactions.map((r, i) => (
              <span key={i} className="text-[10px] bg-black/40 border border-white/10 rounded-full px-2 py-0.5 text-neutral-300">{r}ms</span>
            ))}
          </div>
          {best !== null && <p className="text-xs text-[#d4af37]">Mejor: {best}ms</p>}
          <button onClick={restart} data-testid="reflex-restart" className="btn-gold px-5 py-2 rounded-full text-xs uppercase tracking-widest">
            Jugar de nuevo
          </button>
        </div>
      )}

      {phase === "tooSoon" && (
        <button onClick={restart} className="btn-gold px-5 py-2 rounded-full text-xs uppercase tracking-widest">
          Intentar de nuevo
        </button>
      )}
    </div>
  );
};

/* ========================================================================== */
/* 6. SECUENCIA MÍSTICA (Simon)                                               */
/* ========================================================================== */
const SIMON_GLYPHS = [
  { color: "#7c2d12", glow: "#ea580c", sym: "✦" },
  { color: "#581c87", glow: "#a855f7", sym: "✷" },
  { color: "#065f46", glow: "#10b981", sym: "⚝" },
  { color: "#7f1d1d", glow: "#f59e0b", sym: "✪" },
];

export const SimonGame = () => {
  const [seq, setSeq] = useState([]);
  const [playerIdx, setPlayerIdx] = useState(0);
  const [phase, setPhase] = useState("idle"); // idle | showing | playing | over
  const [active, setActive] = useState(null);
  const [best, setBest] = useState(() => lsGet("game_simon_best", 0));

  const start = () => {
    const first = Math.floor(Math.random() * 4);
    setSeq([first]);
    setPhase("showing");
    setPlayerIdx(0);
  };

  // Show sequence
  useEffect(() => {
    if (phase !== "showing") return undefined;
    let i = 0;
    const showNext = () => {
      if (i >= seq.length) {
        setActive(null);
        setPhase("playing");
        return;
      }
      setActive(seq[i]);
      setTimeout(() => {
        setActive(null);
        setTimeout(showNext, 220);
      }, 460);
      i += 1;
    };
    const t = setTimeout(showNext, 600);
    return () => clearTimeout(t);
  }, [phase, seq]);

  const onPress = (idx) => {
    if (phase !== "playing") return;
    setActive(idx);
    setTimeout(() => setActive(null), 220);
    if (seq[playerIdx] !== idx) {
      // Game over
      const score = seq.length - 1;
      setPhase("over");
      if (score > best) {
        lsSet("game_simon_best", score);
        setBest(score);
        if (score > 0) toast.success(`🏆 ¡Nuevo récord! Nivel ${score}`);
      }
      return;
    }
    if (playerIdx + 1 === seq.length) {
      // Next round
      setPlayerIdx(0);
      setSeq([...seq, Math.floor(Math.random() * 4)]);
      setPhase("showing");
    } else {
      setPlayerIdx(playerIdx + 1);
    }
  };

  const restart = () => {
    setSeq([]); setPlayerIdx(0); setPhase("idle"); setActive(null);
  };

  return (
    <div className="space-y-4 max-w-sm mx-auto text-center" data-testid="game-simon">
      <h2 className="font-serif text-xl text-white">Secuencia Mística</h2>
      <p className="text-xs text-neutral-400">Repite la secuencia que muestre el universo.</p>
      <div className="text-xs text-neutral-300">
        Nivel: <span className="text-[#d4af37] font-bold">{seq.length || 0}</span>
        {best > 0 && <span className="ml-3 text-[#d4af37]"><Trophy className="w-3 h-3 inline" /> {best}</span>}
      </div>

      <div className="grid grid-cols-2 gap-2 max-w-xs mx-auto">
        {SIMON_GLYPHS.map((g, i) => (
          <button
            key={i}
            onClick={() => onPress(i)}
            disabled={phase !== "playing"}
            data-testid={`simon-glyph-${i}`}
            className="aspect-square rounded-2xl border-2 transition-all flex items-center justify-center text-4xl"
            style={{
              background: active === i ? g.glow : g.color,
              borderColor: active === i ? "#fff" : `${g.glow}aa`,
              boxShadow: active === i ? `0 0 40px ${g.glow}` : "none",
              opacity: phase === "idle" || phase === "over" ? 0.55 : 1,
              color: "#fff",
            }}
          >
            {g.sym}
          </button>
        ))}
      </div>

      {(phase === "idle" || phase === "over") && (
        <div className="space-y-2">
          {phase === "over" && <p className="text-sm text-red-300">Llegaste al nivel {seq.length - 1}</p>}
          <button onClick={phase === "over" ? restart : start} data-testid="simon-start" className="btn-gold px-5 py-2.5 rounded-full text-xs uppercase tracking-widest">
            {phase === "over" ? "Reintentar" : "Comenzar"}
          </button>
          {phase === "over" && (
            <button onClick={start} className="ml-2 text-xs text-neutral-300 hover:text-white border border-white/10 rounded-full px-4 py-2 uppercase tracking-widest">
              Nueva partida
            </button>
          )}
        </div>
      )}
      {phase === "showing" && <p className="text-xs text-[#d4af37] animate-pulse">Memoriza...</p>}
      {phase === "playing" && <p className="text-xs text-emerald-300">Tu turno ({playerIdx + 1}/{seq.length})</p>}
    </div>
  );
};

/* ========================================================================== */
/* 7. ADIVINA EL SIGNO                                                        */
/* ========================================================================== */
const ZODIACS = [
  { sign: "Aries",       hints: ["Fuego cardinal", "Lidera sin miedo", "Marte lo rige", "Ímpetu y arranque", "Le encanta competir"] },
  { sign: "Tauro",       hints: ["Tierra fija", "Disfruta del placer y la calma", "Venus lo rige", "Terco pero leal", "Adora la buena mesa"] },
  { sign: "Géminis",     hints: ["Aire mutable", "Curioso y comunicativo", "Mercurio lo rige", "Dos caras, mil ideas", "Hablador incansable"] },
  { sign: "Cáncer",      hints: ["Agua cardinal", "Hogar y familia primero", "La Luna lo rige", "Intuitivo y emocional", "Memoria de elefante"] },
  { sign: "Leo",         hints: ["Fuego fijo", "Brilla en el escenario", "El Sol lo rige", "Generoso y orgulloso", "Le encanta ser admirado"] },
  { sign: "Virgo",       hints: ["Tierra mutable", "Detallista hasta el último punto", "Mercurio lo rige", "Servicial y perfeccionista", "Crítico interno feroz"] },
  { sign: "Libra",       hints: ["Aire cardinal", "Justicia y equilibrio ante todo", "Venus lo rige", "Encanto diplomático", "Le cuesta decidir"] },
  { sign: "Escorpio",    hints: ["Agua fija", "Profundo, intenso, magnético", "Plutón lo rige", "Vive en blanco y negro", "Investigador implacable"] },
  { sign: "Sagitario",   hints: ["Fuego mutable", "Filósofo viajero", "Júpiter lo rige", "Optimista por naturaleza", "Necesita libertad"] },
  { sign: "Capricornio", hints: ["Tierra cardinal", "Constructor incansable", "Saturno lo rige", "Disciplina y ambición", "Maduro desde niño"] },
  { sign: "Acuario",     hints: ["Aire fijo", "Visionario y rebelde", "Urano lo rige", "Original al extremo", "Ama a la humanidad pero no a la gente"] },
  { sign: "Piscis",      hints: ["Agua mutable", "Sensible y soñador", "Neptuno lo rige", "Empatía sin filtros", "Vive en otro plano"] },
];

const buildZodiacQuestion = () => {
  const correct = rand(ZODIACS);
  const hint = rand(correct.hints);
  const others = shuffle(ZODIACS.filter((z) => z.sign !== correct.sign)).slice(0, 3);
  const options = shuffle([correct, ...others]).map((z) => z.sign);
  return { hint, correct: correct.sign, options };
};

export const GuessZodiacGame = () => {
  const [round, setRound] = useState(0);
  const [score, setScore] = useState(0);
  const [q, setQ] = useState(() => buildZodiacQuestion());
  const [selected, setSelected] = useState(null);
  const [done, setDone] = useState(false);
  const [best, setBest] = useState(() => lsGet("game_zodiac_best", 0));
  const TOTAL = 10;

  const pick = (opt) => {
    if (selected) return;
    setSelected(opt);
    const correct = opt === q.correct;
    if (correct) setScore((s) => s + 1);
    setTimeout(() => {
      if (round + 1 >= TOTAL) {
        const final = score + (correct ? 1 : 0);
        setDone(true);
        if (final > best) {
          lsSet("game_zodiac_best", final);
          setBest(final);
          if (final > 0) toast.success(`🏆 ¡Nuevo récord! ${final}/${TOTAL}`);
        }
      } else {
        setRound((r) => r + 1);
        setQ(buildZodiacQuestion());
        setSelected(null);
      }
    }, 900);
  };

  const restart = () => {
    setRound(0); setScore(0); setSelected(null); setDone(false);
    setQ(buildZodiacQuestion());
  };

  if (done) {
    return (
      <div className="text-center py-8 space-y-3" data-testid="zodiac-done">
        <Trophy className="w-12 h-12 text-[#d4af37] mx-auto" />
        <h2 className="font-serif text-2xl text-white">{score}/{TOTAL}</h2>
        <p className="text-xs text-neutral-400">Mejor: {best}</p>
        <button onClick={restart} className="btn-gold mt-3 px-5 py-2 rounded-full text-xs uppercase tracking-widest">Jugar otra vez</button>
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-md mx-auto" data-testid="game-zodiac">
      <div className="text-center">
        <h2 className="font-serif text-xl text-white">Adivina el Signo</h2>
        <p className="text-xs text-neutral-400">Ronda {round + 1} de {TOTAL} · Puntos: {score}</p>
      </div>
      <div className="h-1 bg-black/40 rounded-full overflow-hidden">
        <div className="h-full bg-[#d4af37] transition-all" style={{ width: `${((round + 1) / TOTAL) * 100}%` }} />
      </div>
      <div className="bg-black/40 border border-white/10 rounded-xl p-5 text-center min-h-[88px] flex items-center justify-center">
        <p className="text-white font-serif text-base italic">{q.hint}</p>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {q.options.map((opt) => {
          const isCorrect = opt === q.correct;
          const isSel = selected === opt;
          const showCorrect = selected && isCorrect;
          const showWrong = selected && isSel && !isCorrect;
          return (
            <button
              key={opt}
              onClick={() => pick(opt)}
              disabled={!!selected}
              data-testid={`zodiac-opt-${opt}`}
              className={`px-3 py-2 rounded-lg border text-sm ${
                showCorrect ? "bg-emerald-500/20 border-emerald-400 text-emerald-200" :
                showWrong  ? "bg-red-500/20 border-red-400 text-red-200" :
                "bg-black/40 border-white/10 text-neutral-200 hover:border-[#d4af37]/40"
              }`}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/* ========================================================================== */
/* 8. ADIVINA LA CARTA DE TAROT                                               */
/* ========================================================================== */
const ARCANA = [
  { name: "El Loco",          desc: "Un nuevo comienzo lleno de fe ingenua." },
  { name: "El Mago",          desc: "Tienes las herramientas, atrévete a usarlas." },
  { name: "La Sacerdotisa",   desc: "El conocimiento oculto y la intuición femenina." },
  { name: "La Emperatriz",    desc: "Abundancia, fertilidad y maternidad creativa." },
  { name: "El Emperador",     desc: "Estructura, autoridad y disciplina." },
  { name: "El Sumo Sacerdote",desc: "Tradición, fe y aprendizaje formal." },
  { name: "Los Enamorados",   desc: "Una decisión del corazón te define." },
  { name: "El Carro",         desc: "Voluntad, victoria y movimiento." },
  { name: "La Fuerza",        desc: "Dominio interior sobre el instinto." },
  { name: "El Ermitaño",      desc: "Soledad sabia y búsqueda interna." },
  { name: "La Rueda",         desc: "El ciclo gira; lo de abajo subirá." },
  { name: "La Justicia",      desc: "Causa y efecto, equilibrio kármico." },
  { name: "El Colgado",       desc: "Pausa voluntaria para ver desde otro ángulo." },
  { name: "La Muerte",        desc: "Transformación profunda, fin de un ciclo." },
  { name: "La Templanza",     desc: "Mezcla, paciencia y sanación." },
  { name: "El Diablo",        desc: "Apegos, sombra y deseos materiales." },
  { name: "La Torre",         desc: "Ruptura súbita que libera la verdad." },
  { name: "La Estrella",      desc: "Esperanza, sanación y guía celestial." },
  { name: "La Luna",          desc: "Ilusión, miedos y mensajes en sueños." },
  { name: "El Sol",           desc: "Éxito, claridad y alegría plena." },
  { name: "El Juicio",        desc: "Llamado interior, renovación espiritual." },
  { name: "El Mundo",         desc: "Cierre exitoso de un gran ciclo." },
];

const buildTarotQuestion = () => {
  const correct = rand(ARCANA);
  const others = shuffle(ARCANA.filter((a) => a.name !== correct.name)).slice(0, 3);
  const options = shuffle([correct, ...others]).map((a) => a.name);
  return { desc: correct.desc, correct: correct.name, options };
};

export const GuessTarotGame = () => {
  const [round, setRound] = useState(0);
  const [score, setScore] = useState(0);
  const [q, setQ] = useState(() => buildTarotQuestion());
  const [selected, setSelected] = useState(null);
  const [done, setDone] = useState(false);
  const [best, setBest] = useState(() => lsGet("game_tarot_best", 0));
  const TOTAL = 7;

  const pick = (opt) => {
    if (selected) return;
    setSelected(opt);
    const correct = opt === q.correct;
    if (correct) setScore((s) => s + 1);
    setTimeout(() => {
      if (round + 1 >= TOTAL) {
        const final = score + (correct ? 1 : 0);
        setDone(true);
        if (final > best) {
          lsSet("game_tarot_best", final);
          setBest(final);
          if (final > 0) toast.success(`🏆 ¡Nuevo récord! ${final}/${TOTAL}`);
        }
      } else {
        setRound((r) => r + 1);
        setQ(buildTarotQuestion());
        setSelected(null);
      }
    }, 950);
  };

  const restart = () => {
    setRound(0); setScore(0); setSelected(null); setDone(false);
    setQ(buildTarotQuestion());
  };

  if (done) {
    return (
      <div className="text-center py-8 space-y-3" data-testid="tarot-done">
        <Trophy className="w-12 h-12 text-[#d4af37] mx-auto" />
        <h2 className="font-serif text-2xl text-white">{score}/{TOTAL}</h2>
        <p className="text-xs text-neutral-400">Mejor: {best}</p>
        <button onClick={restart} className="btn-gold mt-3 px-5 py-2 rounded-full text-xs uppercase tracking-widest">Jugar otra vez</button>
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-md mx-auto" data-testid="game-tarot-guess">
      <div className="text-center">
        <h2 className="font-serif text-xl text-white">Adivina la Carta</h2>
        <p className="text-xs text-neutral-400">Ronda {round + 1} de {TOTAL} · Puntos: {score}</p>
      </div>
      <div className="h-1 bg-black/40 rounded-full overflow-hidden">
        <div className="h-full bg-[#d4af37] transition-all" style={{ width: `${((round + 1) / TOTAL) * 100}%` }} />
      </div>
      <div className="bg-gradient-to-br from-[#d4af37]/10 via-purple-900/10 to-black border border-[#d4af37]/30 rounded-xl p-5 text-center min-h-[90px] flex items-center justify-center">
        <p className="text-white font-serif italic">{q.desc}</p>
      </div>
      <div className="grid grid-cols-1 gap-2">
        {q.options.map((opt) => {
          const isCorrect = opt === q.correct;
          const isSel = selected === opt;
          const showCorrect = selected && isCorrect;
          const showWrong = selected && isSel && !isCorrect;
          return (
            <button
              key={opt}
              onClick={() => pick(opt)}
              disabled={!!selected}
              data-testid={`tarot-opt-${opt}`}
              className={`px-3 py-2.5 rounded-lg border text-sm flex items-center justify-between ${
                showCorrect ? "bg-emerald-500/20 border-emerald-400 text-emerald-200" :
                showWrong  ? "bg-red-500/20 border-red-400 text-red-200" :
                "bg-black/40 border-white/10 text-neutral-200 hover:border-[#d4af37]/40"
              }`}
            >
              <span>{opt}</span>
              {showCorrect && <Check className="w-4 h-4" />}
              {showWrong && <XIcon className="w-4 h-4" />}
            </button>
          );
        })}
      </div>
    </div>
  );
};
